import React, { useMemo } from 'react';
import { collection, doc, query, where } from 'firebase/firestore';
import { keyBy } from 'lodash';
import { startOfMonth, endOfMonth, addMonths, setDate, format as formatDate, getDate, getDaysInMonth } from 'date-fns';
import numeral from 'numeral';
import classnames from 'classnames';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

import TenantPage from '../TenantPage';
import QueryBoolean from '../QueryBoolean';
import SetupGuide from '../SetupGuide';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import usePlCalculation from '../hooks/usePlCalculation';

export default function Dashboard(props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
}

function PageContent(props) {
  const { tenant } = props;
  const queryParams = useQueryParams();
  // テナント設定「税抜き表示のデフォルト」をデフォルトに。URLクエリ指定があればそちら優先
  const defaultExcludeTax = tenant.exportExcludeTax === 'excluded';
  const isExcludeTax = queryParams.excludeTax != null ? queryParams.excludeTax === '1' : defaultExcludeTax;
  const { items: sellers, isLoading: isLoadingSellers } = useCollectionSubscription(query(collection(tenant.ref, 'sellers')), [], { detail: true });
  const { items: shops, isLoading: isLoadingShops } = useCollectionSubscription(query(collection(tenant.ref, 'shops')), [], { detail: true });
  const { items: adApiAccounts, isLoading: isLoadingAdApiAccounts } = useCollectionSubscription(query(collection(tenant.ref, 'adApiAccounts')), [], { detail: true });
  const isLoadingSetup = isLoadingSellers || isLoadingShops || isLoadingAdApiAccounts;
  const shopsById = keyBy(shops, 'id');

  const today = new Date();
  const thisMonthStart = startOfMonth(today);
  const thisMonthEnd = endOfMonth(today);
  const lastMonthStart = startOfMonth(addMonths(today, -1));
  const todayDate = getDate(today);
  const lastMonthDays = getDaysInMonth(lastMonthStart);
  const lastMonthEndDay = Math.min(todayDate, lastMonthDays);
  const lastMonthEnd = setDate(lastMonthStart, lastMonthEndDay);

  // 直近6ヶ月分（今月含む）の期間配列
  // 直近6ヶ月（今月含む）+ 前月同期（index=6）の計7期間を1回のフックで取得
  const allDateRanges = useMemo(() => [
    ...Array.from({ length: 6 }, (_, i) => {
      const m = addMonths(today, -(5 - i));
      return [startOfMonth(m), endOfMonth(m)];
    }),
    [lastMonthStart, lastMonthEnd], // index=6: 前月同期
  ], []);

  const trendDateRanges = allDateRanges.slice(0, 6);

  const { periodData } = usePlCalculation({
    dateRanges: allDateRanges,
    sellers,
    dateRangeType: 'monthly',
    isExcludeTax,
  });

  // 今月（index=5）・前月同期（index=6）のデータを取り出す
  const thisMonthPeriod = periodData?.[5];
  const lastMonthPeriod = periodData?.[6];
  const thisItems = thisMonthPeriod?.items ?? {};
  const lastItems = lastMonthPeriod?.items ?? {};
  const thisSales = thisMonthPeriod?.salesAmount ?? 0;

  const thisMonth = formatDate(today, 'yyyy年M月');
  const lastMonth = `${formatDate(addMonths(today, -1), 'M月')}1〜${lastMonthEndDay}日`;

  const topProductsSummary = useDocumentSubscription(doc(tenant.ref, 'dashboardSummaries', 'topProducts'), [tenant.id]);
  const topProducts = (isExcludeTax ? topProductsSummary?.productsExcludeTax : topProductsSummary?.products) ?? [];

  const inventorySummary = useDocumentSubscription(doc(tenant.ref, 'dashboardSummaries', 'inventory'), [tenant.id]);
  const inventoryAmount = (isExcludeTax ? inventorySummary?.inventoryAmountExcludeTax : inventorySummary?.inventoryAmount) ?? 0;

  const topProductIds = topProducts.map(_ => _.productId);
  const topProductDocs = useCollectionsFetch(
    topProductIds.length > 0
      ? [query(collection(tenant.ref, 'products'), where('__name__', 'in', topProductIds))]
      : null,
    [topProductIds.join(',')]
  );
  const topProductDocsById = keyBy(topProductDocs, 'id');

  const kpiRows = [
    { key: 'sales',              label: '売上'    },
    { key: 'grossProfit',        label: '粗利'    },
    { key: 'grossProfit2',       label: '純粗利'  },
    { key: 'contributionMargin', label: '限界利益' },
    { key: 'operatingProfit',    label: '営業利益' },
    { key: 'inventoryAmount',    label: 'FBA在庫額', variant: 'snapshot' },
  ];

  // チャート用データ
  const trendData = useMemo(() => {
    if (!periodData) return [];
    return trendDateRanges.map((range, i) => {
      const period = periodData[i];
      if (!period) return { month: formatDate(range[0], 'M月'), 売上: 0, 純粗利: 0, 限界利益: 0 };
      return {
        month: formatDate(range[0], 'M月'),
        売上: period.items?.sales?.computeValue() ?? 0,
        純粗利: period.items?.grossProfit2?.computeValue() ?? 0,
        限界利益: period.items?.contributionMargin?.computeValue() ?? 0,
      };
    });
  }, [periodData, trendDateRanges]);

  return (
    <div className="dashboard container-fluid">
      <div className="px-4 py-4" style={{ maxWidth: 1200 }}>
        <div className="mb-4 d-flex gap-2 align-items-end">
          <h5 className="text-muted fw-normal mb-0">{thisMonth}の概況</h5>
          <QueryBoolean paramName="excludeTax" label="税抜き" defaultValue={defaultExcludeTax ? '1' : '0'} />
        </div>

        {!tenant.hasDemo && (
          <SetupGuide
            tenant={tenant}
            sellers={sellers}
            shops={shops}
            adApiAccounts={adApiAccounts}
            isLoading={isLoadingSetup}
          />
        )}

        <div className="row g-3 mb-4">
          {kpiRows.map(({ key, label, variant }) => {
            if (variant === 'snapshot') {
              return (
                <KpiCard
                  key={key}
                  label={label}
                  value={inventoryAmount}
                  variant="snapshot"
                />
              );
            }
            return (
              <KpiCard
                key={key}
                label={label}
                lastMonth={lastMonth}
                value={thisItems[key]?.computeValue() ?? 0}
                prevValue={lastItems[key]?.computeValue() ?? 0}
                sales={thisSales}
              />
            );
          })}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-7">
            <TrendChartCard trendData={trendData} />
          </div>
          <div className="col-12 col-lg-5">
            <TopProductsCard products={topProducts} productDocsById={topProductDocsById} shopsById={shopsById} thisMonth={thisMonth} />
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <PlBreakdownCard items={thisItems} sales={thisSales} />
          </div>
          <div className="col-12 col-lg-6">
            <MomComparisonCard
              thisMonth={thisMonth}
              lastMonth={lastMonth}
              thisItems={thisItems}
              lastItems={lastItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, prevValue, sales, variant }) {
  const isSnapshot = variant === 'snapshot';
  const momRate = !isSnapshot && prevValue !== 0 ? (value - prevValue) / Math.abs(prevValue) : null;
  const isPositive = momRate === null || momRate >= 0;
  const salesRate = !isSnapshot && sales > 0 ? value / sales : 0;

  return (
    <div className="col-6 col-lg-4 col-xl-2">
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-body p-3">
          <div className="text-muted small mb-1">{label}</div>
          <div className={classnames('fs-5 fw-bold', { 'text-danger': value < 0 })}>
            {numeral(value).format()}円
          </div>
          {!isSnapshot && sales > 0 && (
            <div className="text-muted small">{numeral(salesRate).format('0.0%')}</div>
          )}
          {momRate !== null && (
            <div className={classnames('small mt-2', isPositive ? 'text-success' : 'text-danger')}>
              {isPositive ? '▲' : '▼'} {numeral(Math.abs(momRate)).format('0.0%')}
              <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>前月同期比</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatYAxis(value) {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card border-0 shadow" style={{ fontSize: '0.8rem', minWidth: 160 }}>
      <div className="card-body p-2">
        <div className="fw-bold mb-1">{label}</div>
        {payload.map(p => (
          <div key={p.name} className="d-flex justify-content-between gap-3">
            <span style={{ color: p.color }}>{p.name}</span>
            <span>{numeral(p.value).format()}円</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChartCard({ trendData }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body p-3">
        <div className="text-muted small mb-3">直近6ヶ月の推移</div>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={trendData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="#dee2e6" />
            <Bar dataKey="売上" fill="#a8c8f0" radius={[3, 3, 0, 0]} maxBarSize={48} />
            <Line type="monotone" dataKey="純粗利" stroke="#4a9eff" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="限界利益" stroke="#20c997" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const plRows = [
  { key: 'sales',              label: '売上',         bold: false },
  { key: 'refund',             label: '売上返金',     bold: false },
  { key: 'cods',               label: '原価',         bold: false },
  { key: 'grossProfit',        label: '粗利',         bold: true  },
  { key: 'cods2',              label: '原価2',        bold: false },
  { key: 'grossProfit2',       label: '純粗利',       bold: true  },
  { key: 'variableSga',        label: '販管費(変動)', bold: false },
  { key: 'contributionMargin', label: '限界利益',     bold: true  },
  { key: 'fixedSga',           label: '販管費(固定)', bold: false },
  { key: 'operatingProfit',    label: '営業利益',     bold: true  },
];

function PlBreakdownCard({ items, sales }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body p-3">
        <div className="text-muted small mb-3">今月の損益内訳</div>
        <table className="table table-sm mb-0">
          <tbody>
            {plRows.map(({ key, label, bold }) => {
              const item = items[key];
              if (!item) return null;
              const value = item.computeValue();
              const rate = sales > 0 ? value / sales : 0;
              return (
                <tr key={key}>
                  <td className={classnames({ 'fw-bold': bold })} style={{ fontSize: '0.85rem' }}>{label}</td>
                  <td className={classnames('text-end', { 'fw-bold': bold, 'text-danger': value < 0 })} style={{ fontSize: '0.85rem' }}>
                    {numeral(value).format()}円
                  </td>
                  <td className="text-end text-muted" style={{ fontSize: '0.75rem', width: 50 }}>
                    {sales > 0 ? numeral(Math.abs(rate)).format('0.0%') : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TopProductsCard({ products, productDocsById, shopsById, thisMonth }) {
  const maxSales = products[0]?.kpis?.sales ?? 0;

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body p-3">
        <div className="text-muted small mb-3">{thisMonth}の上位売上商品 TOP5</div>
        {products.length === 0 ? (
          <div className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>データなし</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {products.map((product, i) => {
              const sales = product.kpis?.sales ?? 0;
              const contributionMargin = product.kpis?.contributionMargin ?? 0;
              const contributionMarginRate = product.kpis?.contributionMarginRate ?? 0;
              const barWidth = maxSales > 0 ? (sales / maxSales) * 100 : 0;
              const productDoc = productDocsById?.[product.productId];
              const amazonImageUrl = productDoc?.amazonAttributes?.summaries?.[0]?.mainImage?.link;
              const rakutenImageLocation = productDoc?.rakutenAttributes?.images?.[0]?.location;
              const rakutenShopUrl = shopsById?.[product.shopId]?.rakutenShopUrl;
              const imageUrl = amazonImageUrl || (rakutenImageLocation && rakutenShopUrl ? `https://image.rakuten.co.jp/${rakutenShopUrl}/cabinet${rakutenImageLocation}` : null);

              return (
                <div key={product.productId} className="d-flex align-items-center gap-3">
                  <div className="text-muted fw-bold" style={{ fontSize: '0.8rem', width: 16, flexShrink: 0 }}>{i + 1}</div>
                  {imageUrl ? (
                    <img src={imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 36, height: 36, flexShrink: 0, background: '#f0f0f0', borderRadius: 4 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-truncate" style={{ fontSize: '0.82rem' }}>{product.name}</div>
                    <div style={{ marginTop: 2, height: 4, background: '#e9ecef', borderRadius: 2 }}>
                      <div style={{ width: `${barWidth}%`, height: '100%', background: '#a8c8f0', borderRadius: 2 }} />
                    </div>
                  </div>
                  <div className="text-end" style={{ flexShrink: 0, minWidth: 90 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{numeral(sales).format()}円</div>
                    <div className={classnames('small', { 'text-danger': contributionMargin < 0, 'text-muted': contributionMargin >= 0 })} style={{ fontSize: '0.75rem' }}>
                      限界利益 {numeral(contributionMarginRate).format('0.0%')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const comparisonKeys = [
  { key: 'sales',              label: '売上'    },
  { key: 'grossProfit',        label: '粗利'    },
  { key: 'grossProfit2',       label: '純粗利'  },
  { key: 'contributionMargin', label: '限界利益' },
  { key: 'operatingProfit',    label: '営業利益' },
];

function MomComparisonCard({ thisMonth, lastMonth, thisItems, lastItems }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body p-3">
        <div className="text-muted small mb-3">前月比較</div>
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}></th>
              <th className="text-end text-muted" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>{lastMonth}</th>
              <th className="text-end text-muted" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>{thisMonth}</th>
              <th className="text-end text-muted" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>前月同期比</th>
            </tr>
          </thead>
          <tbody>
            {comparisonKeys.map(({ key, label }) => {
              const thisItem = thisItems[key];
              const lastItem = lastItems[key];
              if (!thisItem) return null;
              const thisVal = thisItem.computeValue();
              const lastVal = lastItem?.computeValue() ?? 0;
              const momRate = lastVal !== 0 ? (thisVal - lastVal) / Math.abs(lastVal) : null;
              const isPositive = momRate === null || momRate >= 0;
              return (
                <tr key={key}>
                  <td style={{ fontSize: '0.85rem' }}>{label}</td>
                  <td className={classnames('text-end', { 'text-danger': lastVal < 0 })} style={{ fontSize: '0.85rem' }}>
                    {numeral(lastVal).format()}
                  </td>
                  <td className={classnames('text-end', { 'text-danger': thisVal < 0 })} style={{ fontSize: '0.85rem' }}>
                    {numeral(thisVal).format()}
                  </td>
                  <td className={classnames('text-end', isPositive ? 'text-success' : 'text-danger')} style={{ fontSize: '0.85rem' }}>
                    {momRate !== null ? `${isPositive ? '▲' : '▼'} ${numeral(Math.abs(momRate)).format('0.0%')}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
